// YtreLoadGateModal.jsx — Iter160e (Randy pt-ytre-p1).
//
// The Smart CTA that gates the URL LOAD button on the YTRE tab when the
// user hasn't connected YouTube for this silo yet. Two behaviors:
//
//   1. If the user has OAuth on ANOTHER silo or channel_slot (fetched
//      via GET /youtube/integrations/mine), offer a one-tap mirror.
//      Fills in the credentials on this silo's channel1 row, then
//      pops the setup modal for the fresh Google sign-in.
//
//   2. If no OAuth anywhere, open the YTRE setup modal directly so the
//      user can paste credentials + finish OAuth from scratch.
//
// User dismissing this modal closes it without loading the URL — they
// have to connect first. This paves the way for Wave 2's comment-preload
// flow (which also requires a live channel1 OAuth row on this silo to
// call commentThreads.list).

import { useState } from "react";
import { toast } from "sonner";
import { X, LinkSimple, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import YTRepliesSetupModal from "@/components/YTRepliesSetupModal";

export default function YtreLoadGateModal({ siloId, alternatives = [], onClose, onConnected }) {
  const [setupOpen, setSetupOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const modLabel = (slot) => (slot === "channel1" ? "Workbench YT Replies / YTRE" : "Refinery YT Replies");
  const usable = (alternatives || []).filter((r) => r.has_client_creds);

  const mirror = async (row) => {
    setBusyId(row.id);
    try {
      const r = await api.post("/youtube/setup/mirror-from-integration", {
        source_integration_id: row.id,
        target_silo_id: siloId,
        target_channel_slot: "channel1",
      });
      // Open the fresh Google consent URL in a NEW TAB (Randy: opens up
      // a separate tab so the user's current YTRE workspace isn't
      // interrupted).
      if (r.data?.authorization_url) {
        window.open(r.data.authorization_url, "_blank", "noopener");
      }
      toast.success(`Mirrored credentials from ${row.youtube_channel_name || "your other channel"}. Complete Google sign-in in the new tab.`);
      onConnected && onConnected();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mirror failed.");
    } finally { setBusyId(null); }
  };

  return (
    <div
      className="fixed inset-0 z-[250] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4"
      onClick={onClose}
      data-testid="ytre-load-gate-modal"
    >
      <div
        className="bg-[#FDFCF8] rounded-2xl w-full max-w-md flex flex-col shadow-2xl border hairline overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b hairline shrink-0 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">Connect first</div>
            <h2 className="font-serif-display text-lg text-[#1C1B1A]">Hook up YouTube to load videos</h2>
          </div>
          <button onClick={onClose} data-testid="ytre-load-gate-close" className="p-1 rounded hover:bg-[#F0F1EE]">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-[13px] text-[#1C1B1A] leading-relaxed">
            YTRE needs your YouTube channel connected on this silo before we can pull a video in. This lets YTRE post your master reply and (later) auto-load your existing comment on that video.
          </p>

          {usable.length > 0 && (
            <>
              <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#2F6B3A] font-semibold mt-3">
                Already connected elsewhere — mirror in one tap
              </div>
              <div className="space-y-1.5">
                {usable.slice(0, 4).map((row) => {
                  const label = `${row.youtube_channel_name || "Channel"} on ${row.silo_name || row.silo_id?.slice(0, 8)} · ${modLabel(row.channel_slot)}`;
                  return (
                    <button
                      key={row.id}
                      disabled={!!busyId}
                      onClick={() => mirror(row)}
                      data-testid={`ytre-load-gate-mirror-${row.id}`}
                      className="w-full text-left text-[12.5px] px-3 py-2 rounded-md border border-[#B9D4BE] bg-[#EAF3EC] hover:bg-[#DCEBDF] disabled:opacity-50 flex items-center gap-2"
                    >
                      <ArrowRight size={12} weight="bold" className="text-[#2F6B3A] shrink-0" />
                      <span className="truncate">
                        {busyId === row.id ? "Mirroring…" : "Use "} <strong className="font-semibold">{label}</strong>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-[#6B6A68] italic pt-1">
                You&apos;ll still complete a fresh Google sign-in for this silo — that binds the YouTube channel identity separately, even though the app credentials are shared.
              </div>
              <div className="border-t hairline pt-3 mt-3" />
            </>
          )}

          <button
            onClick={() => setSetupOpen(true)}
            data-testid="ytre-load-gate-connect-fresh"
            className="w-full text-[12.5px] px-3 py-2.5 rounded-md bg-[#B54848] text-white hover:bg-[#8f3838] flex items-center justify-center gap-1.5 font-medium"
          >
            <LinkSimple size={12} weight="bold" />
            {usable.length > 0 ? "Or set up a different channel from scratch" : "Set up YouTube on this silo"}
          </button>

          <button
            onClick={onClose}
            data-testid="ytre-load-gate-dismiss"
            className="w-full text-[11px] text-[#6B6A68] hover:text-[#1C1B1A] pt-1"
          >
            Cancel — I&apos;ll connect first, then try Load again.
          </button>
        </div>
      </div>

      {setupOpen && (
        <YTRepliesSetupModal
          open={setupOpen}
          onClose={() => { setSetupOpen(false); }}
          channelSlot="channel1"
          siloId={siloId}
          surfaceTitle="AI YouTube YTRE — Setup"
        />
      )}
    </div>
  );
}
